import { useEffect, useState } from "react";
import LangDropdown from "./LangDropdown";
import NewsletterModal from "./NewsletterModal";
import { icons } from "./assets";
import { subscribeToNewsletter } from "./api";
import { applySeo } from "./seo";

const NEWSLETTER_DISMISSED_KEY = "mizu-newsletter-dismissed";
const NEWSLETTER_SUBSCRIBED_KEY = "mizu-newsletter-subscribed";

const copy = {
  hu: {
    langLabel: "Nyelv",
    brandAria: "Mizu kezdőlap",
    logoAlt: "Mizu logó",
    brandSub: "Brunch & Coffee",
    heading: "Megnyitottunk!",
    subtitle: "Brunch & kávé Budapest szívében — gyere el hozzánk a Fáy utcába!",
    location: "Budapest, Fáy utca 75, 1135",
    features: [
      { icon: "cup", label: "Brunch & kávé" },
      { icon: "location", label: "Budapest" },
      { icon: "clock", label: "H–V: 7:00 – 17:30" },
    ],
    cta: "Feliratkozás",
    follow: "Kövess minket Instagramon!",
    newsletter: {
      title: "Megnyitottunk!",
      subtitle: "Iratkozz fel híreinkre, ajánlatainkra és meglepetéseinkre.",
      emailLabel: "E-mail cím",
      emailPlaceholder: "E-mail címed",
      submit: "Feliratkozom",
      success: "Köszönjük, hogy feliratkoztál!",
      error: "Nem sikerült feliratkozni. Próbáld újra később.",
      closeLabel: "Bezárás",
    },
    footer: {
      addressTitle: "Cím",
      address: "Budapest, Fáy utca 75, 1135",
      emailTitle: "E-mail",
      socialTitle: "Kövess minket",
      social: "@mizubudapest",
      socialUrl: "https://www.instagram.com/mizubudapest/",
      copyright: "© 2026 Mizu Brunch & Coffee · Minden jog fenntartva.",
    },
  },
  en: {
    langLabel: "Language",
    brandAria: "Mizu home",
    logoAlt: "Mizu logo",
    brandSub: "Brunch & Coffee",
    heading: "We're Open!",
    subtitle: "Brunch & coffee in the heart of Budapest — see you on Fáy utca!",
    location: "Budapest, Fáy utca 75, 1135",
    features: [
      { icon: "cup", label: "Brunch & Coffee" },
      { icon: "location", label: "Budapest" },
      { icon: "clock", label: "Mon – Sun: 7:00 – 17:30" },
    ],
    cta: "Subscribe",
    follow: "Follow us on Instagram!",
    newsletter: {
      title: "We're Open!",
      subtitle: "Sign up for our news, offers, and subscriber surprises.",
      emailLabel: "Email address",
      emailPlaceholder: "Your email address",
      submit: "Subscribe",
      success: "Thanks for subscribing!",
      error: "Could not subscribe. Please try again later.",
      closeLabel: "Close",
    },
    footer: {
      addressTitle: "Address",
      address: "Budapest, Fáy utca 75, 1135",
      emailTitle: "Email",
      socialTitle: "Follow Us",
      social: "@mizubudapest",
      socialUrl: "https://www.instagram.com/mizubudapest/",
      copyright: "© 2026 Mizu Brunch & Coffee · All rights reserved.",
    },
  },
};

function ComingSoon() {
  const [lang, setLang] = useState("hu");
  const [newsletterOpen, setNewsletterOpen] = useState(false);
  const [newsletterStatus, setNewsletterStatus] = useState("idle");
  const t = copy[lang];

  useEffect(() => {
    applySeo(lang);
  }, [lang]);

  useEffect(() => {
    const dismissed = localStorage.getItem(NEWSLETTER_DISMISSED_KEY);
    const subscribed = localStorage.getItem(NEWSLETTER_SUBSCRIBED_KEY);
    if (dismissed || subscribed) return;

    const timer = window.setTimeout(() => {
      setNewsletterOpen(true);
    }, 4500);

    return () => window.clearTimeout(timer);
  }, []);

  function openNewsletter() {
    setNewsletterStatus("idle");
    setNewsletterOpen(true);
  }

  function closeNewsletter() {
    setNewsletterOpen(false);
    localStorage.setItem(NEWSLETTER_DISMISSED_KEY, "1");
  }

  async function handleNewsletterSubmit(event) {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const email = String(formData.get("email") || "").trim();

    setNewsletterStatus("loading");

    try {
      await subscribeToNewsletter(email, lang);

      localStorage.setItem(NEWSLETTER_SUBSCRIBED_KEY, email);
      localStorage.setItem(NEWSLETTER_DISMISSED_KEY, "1");
      setNewsletterStatus("success");

      window.setTimeout(() => {
        setNewsletterOpen(false);
        setNewsletterStatus("idle");
      }, 2200);
    } catch {
      setNewsletterStatus("error");
    }
  }

  return (
    <div className="coming-soon">
      <header className="coming-soon-topbar container">
        <a href="#" className="coming-soon-brand" aria-label={t.brandAria}>
          <img src={icons.logo} alt="" className="coming-soon-brand-logo" />
          <span className="coming-soon-brand-sub">{t.brandSub}</span>
        </a>

        <LangDropdown lang={lang} onChange={setLang} ariaLabel={t.langLabel} />
      </header>

      <main className="coming-soon-main">
        <img src={icons.logo} alt={t.logoAlt} className="coming-soon-logo" />

        <p className="coming-soon-brand-line">
          <span aria-hidden="true" />
          {t.brandSub}
          <span aria-hidden="true" />
        </p>

        <h1 className="coming-soon-heading">{t.heading}</h1>
        <p className="coming-soon-subtitle">{t.subtitle}</p>

        <p className="coming-soon-location">
          <img src={icons.location} alt="" className="coming-soon-inline-icon" />
          {t.location}
        </p>

        <div className="coming-soon-features">
          {t.features.map((item, index) => (
            <div key={item.label} className="coming-soon-feature">
              {index > 0 && <span className="coming-soon-feature-divider" aria-hidden="true" />}
              <img
                src={icons[item.icon]}
                alt=""
                className="coming-soon-feature-icon"
              />
              <span className="coming-soon-feature-label">{item.label}</span>
            </div>
          ))}
        </div>

        <button type="button" className="coming-soon-cta" onClick={openNewsletter}>
          {t.cta}
        </button>

        <p className="coming-soon-follow">{t.follow}</p>
      </main>

      <NewsletterModal
        open={newsletterOpen}
        onClose={closeNewsletter}
        copy={t.newsletter}
        onSubmit={handleNewsletterSubmit}
        status={newsletterStatus}
      />

      <footer className="coming-soon-footer">
        <div className="coming-soon-footer-grid container">
          <div className="coming-soon-footer-col">
            <img src={icons.location} alt="" className="coming-soon-footer-icon" />
            <strong>{t.footer.addressTitle}</strong>
            <span>{t.footer.address}</span>
          </div>

          <div className="coming-soon-footer-col">
            <img src={icons.mail} alt="" className="coming-soon-footer-icon" />
            <strong>{t.footer.emailTitle}</strong>
            <a href="mailto:info@mizubrunch.hu">info@mizubrunch.hu</a>
          </div>

          <div className="coming-soon-footer-col">
            <img src={icons.insta} alt="" className="coming-soon-footer-icon" />
            <strong>{t.footer.socialTitle}</strong>
            <a
              href={t.footer.socialUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t.footer.social}
            </a>
          </div>
        </div>

        <p className="coming-soon-copyright">{t.footer.copyright}</p>
      </footer>
    </div>
  );
}

export default ComingSoon;
